import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { useI18n } from "@/i18n";
import { sanitizeHtml } from "@/lib/sanitize";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft,
  Phone,
  MessageCircle,
  Clock,
  MapPin,
  Star,
  ShieldCheck,
  Car,
  Plane,
  Ship,
  ChevronRight,
} from "lucide-react";

const PAGE_URL = "https://galluradrive.it/ncc-santa-teresa-gallura";
const CAR_IMG =
  "https://customer-assets.emergentagent.com/job_gallura-auto-rental/artifacts/vfttlow5_1782508931419.jpg.jpg";

/**
 * Dedicated SEO landing page targeting the keyword
 * "NCC Santa Teresa Gallura" / "Taxi NCC Santa Teresa Gallura".
 *
 * Bilingual (IT/EN), FAQ Schema, Service Schema, LocalBusiness Schema.
 * All meta tags are injected on mount via useEffect.
 */
export default function NccSantaTeresaPage() {
  const { lang, setLang } = useI18n();
  const c = lang === "it" ? IT : EN;

  // Inject SEO meta tags + JSON-LD schemas on mount
  useEffect(() => {
    document.title = c.metaTitle;

    const upsertMeta = (attr, key, value) => {
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", value);
    };
    const upsertLink = (rel, hrefValue, hreflang) => {
      const selector = hreflang
        ? `link[rel="${rel}"][hreflang="${hreflang}"]`
        : `link[rel="${rel}"]`;
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        if (hreflang) el.setAttribute("hreflang", hreflang);
        document.head.appendChild(el);
      }
      el.setAttribute("href", hrefValue);
    };

    upsertMeta("name", "description", c.metaDescription);
    upsertMeta(
      "name",
      "keywords",
      "ncc santa teresa gallura, taxi santa teresa gallura, taxi ncc santa teresa gallura, autista privato santa teresa gallura, transfer olbia santa teresa gallura, transfer aeroporto olbia santa teresa gallura, ncc gallura, ncc costa smeralda, ncc olbia, ncc palau, ncc cannigione, ncc baja sardinia, transfer bonifacio santa teresa, transfer bonifacio sardegna, taxi capo testa, taxi porto cervo, taxi la maddalena, autista privato costa smeralda, chauffeur gallura, private driver sardinia, private driver santa teresa gallura"
    );
    upsertMeta("property", "og:title", c.metaTitle);
    upsertMeta("property", "og:description", c.metaDescription);
    upsertMeta("property", "og:url", PAGE_URL);
    upsertMeta("property", "og:image", CAR_IMG);
    upsertMeta("property", "og:type", "website");
    upsertMeta("name", "twitter:title", c.metaTitle);
    upsertMeta("name", "twitter:description", c.metaDescription);
    upsertMeta("name", "twitter:image", CAR_IMG);
    upsertLink("canonical", PAGE_URL);
    upsertLink("alternate", PAGE_URL, "it");
    upsertLink("alternate", `${PAGE_URL}?lang=en`, "en");
    upsertLink("alternate", PAGE_URL, "x-default");

    // JSON-LD injections (Service, FAQPage, BreadcrumbList)
    const buildLd = (id, data) => {
      let el = document.getElementById(id);
      if (!el) {
        el = document.createElement("script");
        el.type = "application/ld+json";
        el.id = id;
        document.head.appendChild(el);
      }
      el.textContent = JSON.stringify(data);
    };

    buildLd("ld-service-sts", {
      "@context": "https://schema.org",
      "@type": "Service",
      serviceType: "NCC — Noleggio Con Conducente",
      name: "NCC Santa Teresa Gallura — Autista Privato",
      description:
        "Servizio NCC 24/7 a Santa Teresa Gallura con Chrysler 300C Touring blu. Transfer dall'aeroporto di Olbia, dal porto di Golfo Aranci, tour privati e collegamenti con Bonifacio.",
      provider: {
        "@type": "LocalBusiness",
        name: "Gallura Drive",
        telephone: "+39 347 001 1246",
        image: CAR_IMG,
        priceRange: "€€",
        address: {
          "@type": "PostalAddress",
          addressLocality: "Santa Teresa Gallura",
          addressRegion: "SS",
          postalCode: "07028",
          addressCountry: "IT",
        },
        geo: {
          "@type": "GeoCoordinates",
          latitude: 41.24075,
          longitude: 9.18877,
        },
      },
      areaServed: [
        { "@type": "City", name: "Santa Teresa Gallura" },
        { "@type": "Place", name: "Capo Testa" },
        { "@type": "Place", name: "Rena Bianca" },
        { "@type": "City", name: "Palau" },
        { "@type": "City", name: "Cannigione" },
        { "@type": "City", name: "Baja Sardinia" },
        { "@type": "City", name: "Porto Cervo" },
        { "@type": "City", name: "Olbia" },
        { "@type": "City", name: "Bonifacio" },
      ],
      offers: {
        "@type": "AggregateOffer",
        priceCurrency: "EUR",
        lowPrice: "40",
        highPrice: "180",
        offerCount: "6",
      },
    });

    buildLd("ld-faq-sts", {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: c.faq.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });

    buildLd("ld-breadcrumb-sts", {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Gallura Drive",
          item: "https://galluradrive.it/",
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "NCC Santa Teresa Gallura",
          item: PAGE_URL,
        },
      ],
    });

    return () => {
      // On unmount, remove injected LD nodes so they don't leak into other routes
      ["ld-service-sts", "ld-faq-sts", "ld-breadcrumb-sts"].forEach((id) => {
        const n = document.getElementById(id);
        if (n) n.remove();
      });
    };
  }, [c, lang]);

  return (
    <div data-testid="ncc-santa-teresa-page" className="min-h-screen bg-[#F9F7F3]">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 px-6 py-4 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <Link
            to="/"
            data-testid="sts-back-home"
            className="flex items-center gap-2 text-slate-700 hover:brand-text transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="font-display text-lg">Gallura Drive</span>
          </Link>
          <div className="flex items-center gap-3">
            <button
              data-testid="sts-lang-toggle"
              onClick={() => setLang(lang === "it" ? "en" : "it")}
              className="text-sm text-slate-600 hover:brand-text uppercase tracking-wider"
            >
              {lang === "it" ? "EN" : "IT"}
            </button>
            <a
              href="tel:+393470011246"
              data-testid="sts-header-call"
              className="hidden sm:inline-flex items-center gap-2 brand-bg text-white rounded-full px-4 py-2 text-sm hover:opacity-90 transition-opacity"
            >
              <Phone className="w-4 h-4" />
              +39 347 001 1246
            </a>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 py-14 lg:py-20 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <div className="uppercase tracking-widest text-xs text-slate-500 mb-4">
            {c.eyebrow}
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl brand-text leading-tight">
            {c.h1}
          </h1>
          <p className="mt-6 text-slate-700 leading-relaxed text-lg max-w-xl">
            {c.lead}
          </p>

          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/#booking" data-testid="sts-cta-book">
              <Button className="brand-bg hover:opacity-90 text-white rounded-full px-6 h-12">
                {c.ctaBook}
              </Button>
            </Link>
            <a
              href="https://wa.me/393470011246"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="sts-cta-whatsapp"
              className="inline-flex items-center gap-2 rounded-full px-6 h-12 border border-slate-300 hover:border-slate-500 transition-colors"
            >
              <MessageCircle className="w-4 h-4" />
              WhatsApp
            </a>
          </div>

          <ul className="mt-10 grid grid-cols-2 gap-4 text-sm text-slate-700">
            <li className="flex items-start gap-2">
              <Clock className="w-4 h-4 mt-0.5 brand-text" />
              {c.f1}
            </li>
            <li className="flex items-start gap-2">
              <ShieldCheck className="w-4 h-4 mt-0.5 brand-text" />
              {c.f2}
            </li>
            <li className="flex items-start gap-2">
              <Car className="w-4 h-4 mt-0.5 brand-text" />
              {c.f3}
            </li>
            <li className="flex items-start gap-2">
              <Star className="w-4 h-4 mt-0.5 brand-text" />
              {c.f4}
            </li>
          </ul>
        </div>

        <div className="relative">
          <img
            src={CAR_IMG}
            alt="Chrysler 300C Touring blu — NCC Santa Teresa Gallura"
            className="w-full rounded-2xl shadow-xl object-cover aspect-[4/3]"
            loading="eager"
          />
          <div className="absolute -bottom-5 left-6 right-6 bg-white rounded-xl shadow-lg px-5 py-4 flex items-center gap-3">
            <MapPin className="w-5 h-5 brand-text" />
            <div className="text-sm">
              <div className="font-medium text-slate-800">
                Santa Teresa Gallura · Sardegna
              </div>
              <div className="text-slate-500">{c.zone}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Intro paragraph — SEO body copy */}
      <section className="max-w-4xl mx-auto px-6 py-14">
        <h2 className="font-display text-3xl brand-text mb-6">{c.introTitle}</h2>
        <div
          className="prose prose-slate max-w-none text-slate-700 leading-relaxed space-y-4"
          dangerouslySetInnerHTML={sanitizeHtml(c.intro)}
        />
      </section>

      {/* Tratte / Routes */}
      <section className="bg-white border-y border-slate-200">
        <div className="max-w-6xl mx-auto px-6 py-16">
          <div className="uppercase tracking-widest text-xs text-slate-500 mb-2">
            {c.routesEyebrow}
          </div>
          <h2 className="font-display text-3xl brand-text mb-10">
            {c.routesTitle}
          </h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {c.routes.map((r) => (
              <div
                key={r.title}
                data-testid={`sts-route-card-${r.title}`}
                className="border border-slate-200 rounded-xl p-6 bg-[#F9F7F3] hover:border-slate-400 transition-colors"
              >
                <div className="flex items-center gap-2 text-slate-500 text-xs uppercase tracking-widest mb-3">
                  {r.icon}
                  {r.type}
                </div>
                <div className="font-display text-lg brand-text mb-1">
                  {r.title}
                </div>
                <div className="text-sm text-slate-600 mb-4">{r.desc}</div>
                <div className="flex items-baseline justify-between border-t border-slate-200 pt-3">
                  <span className="text-xs text-slate-500">{c.from}</span>
                  <span className="font-display text-2xl brand-text">
                    {r.price}
                  </span>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-10 text-center">
            <Link to="/#booking" data-testid="sts-routes-book">
              <Button className="brand-bg hover:opacity-90 text-white rounded-full px-8 h-12">
                {c.ctaBook}
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why choose us */}
      <section className="max-w-6xl mx-auto px-6 py-16">
        <h2 className="font-display text-3xl brand-text mb-10">{c.whyTitle}</h2>
        <div className="grid md:grid-cols-3 gap-6">
          {c.why.map((w) => (
            <div
              key={w.title}
              className="rounded-xl border border-slate-200 bg-white p-6"
            >
              <div className="w-10 h-10 rounded-full brand-bg text-white grid place-items-center mb-4">
                {w.icon}
              </div>
              <div className="font-display text-lg brand-text mb-2">
                {w.title}
              </div>
              <p className="text-sm text-slate-600 leading-relaxed">{w.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white border-t border-slate-200">
        <div className="max-w-4xl mx-auto px-6 py-16">
          <div className="uppercase tracking-widest text-xs text-slate-500 mb-2">
            FAQ
          </div>
          <h2 className="font-display text-3xl brand-text mb-8">
            {c.faqTitle}
          </h2>
          <div className="space-y-4">
            {c.faq.map((f) => (
              <details
                key={f.q}
                data-testid={`sts-faq-${f.q.slice(0, 20)}`}
                className="group border border-slate-200 rounded-xl bg-[#F9F7F3] px-6 py-4 open:bg-white transition-colors"
              >
                <summary className="cursor-pointer font-display text-lg brand-text list-none flex items-start justify-between gap-4">
                  <span>{f.q}</span>
                  <ChevronRight className="w-5 h-5 shrink-0 mt-1 transition-transform group-open:rotate-90" />
                </summary>
                <p className="mt-3 text-slate-700 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="brand-bg text-white">
        <div className="max-w-4xl mx-auto px-6 py-16 text-center">
          <h2 className="font-display text-3xl sm:text-4xl mb-4">
            {c.finalTitle}
          </h2>
          <p className="text-white/80 max-w-xl mx-auto mb-8">{c.finalDesc}</p>
          <div className="flex flex-wrap gap-3 justify-center">
            <a
              href="tel:+393470011246"
              data-testid="sts-final-call"
              className="inline-flex items-center gap-2 bg-white brand-text rounded-full px-6 h-12 hover:opacity-90 transition-opacity font-medium"
            >
              <Phone className="w-4 h-4" />
              +39 347 001 1246
            </a>
            <a
              href="https://wa.me/393470011246"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="sts-final-whatsapp"
              className="inline-flex items-center gap-2 border border-white/40 text-white rounded-full px-6 h-12 hover:bg-white/10 transition-colors"
            >
              <MessageCircle className="w-4 h-4" />
              WhatsApp
            </a>
            <Link
              to="/#booking"
              data-testid="sts-final-book"
              className="inline-flex items-center gap-2 accent-bg text-[#0A2463] rounded-full px-6 h-12 hover:opacity-90 transition-opacity font-medium"
            >
              {c.ctaBook}
              <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

/* ---------------- CONTENT — ITALIAN ---------------- */
const IT = {
  metaTitle:
    "NCC Santa Teresa Gallura | Taxi Autista Privato 24/7 · Gallura Drive",
  metaDescription:
    "NCC a Santa Teresa Gallura con Chrysler 300C blu. Transfer aeroporto Olbia, Bonifacio, Palau, Costa Smeralda. Prenota online, pagamento in auto. Servizio 24/7.",
  eyebrow: "Servizio NCC · Santa Teresa Gallura",
  h1: "NCC Santa Teresa Gallura — Il tuo autista privato in Alta Gallura",
  lead:
    "Servizio di noleggio con conducente (NCC) 24/7 a Santa Teresa Gallura. Transfer eleganti a bordo della nostra Chrysler 300C Touring blu: aeroporto di Olbia, porto di Golfo Aranci, collegamento con Bonifacio, tour privati verso Capo Testa, Rena Bianca e tutta la Costa Smeralda.",
  zone: "Alta Gallura · Costa Smeralda · Bocche di Bonifacio",
  f1: "Disponibile 24 ore su 24",
  f2: "Autista NCC autorizzato",
  f3: "Chrysler 300C · fino a 4 passeggeri",
  f4: "Recensioni verificate",
  ctaBook: "Prenota ora",
  introTitle:
    "Autista privato a Santa Teresa Gallura: eleganza americana, ospitalità sarda",
  intro: `
    <p>Cerchi un servizio <strong>NCC a Santa Teresa Gallura</strong> puntuale, professionale e con un'auto che faccia la differenza? <strong>Gallura Drive</strong> è il tuo autista privato di fiducia nell'Alta Gallura. Ti accompagniamo a bordo di una <strong>Chrysler 300C Touring blu notte</strong>: una berlina station wagon americana con interni in pelle, climatizzazione automatica e ampio bagagliaio, perfetta per famiglie, coppie e piccoli gruppi fino a 4 passeggeri.</p>

    <p>Rispetto al tradizionale taxi, il servizio NCC (Noleggio Con Conducente) garantisce <strong>tariffa concordata prima della partenza</strong>, prenotazione anticipata, veicolo dedicato e un autista in giacca e cravatta che ti accoglie con l'acqua omaggio a bordo e Wi-Fi disponibile. Nessuna sorpresa sul prezzo, nessuna attesa: la vettura arriva all'orario stabilito, al luogo che scegli tu — davanti all'hotel, in aeroporto, al porto o al ristorante.</p>

    <p>Operiamo tutti i giorni dell'anno, 24 ore su 24, con partenza da <strong>Santa Teresa Gallura</strong> e collegamenti diretti verso <strong>Olbia Costa Smeralda Airport</strong>, il <strong>porto di Golfo Aranci</strong>, <strong>Bonifacio</strong> (Corsica) tramite traghetto, <strong>Palau</strong>, <strong>Cannigione</strong>, <strong>Baja Sardinia</strong>, <strong>Porto Cervo</strong> e le spiagge più belle della zona: Rena Bianca, Capo Testa, La Marmorata, Cala Spinosa.</p>

    <p>Il pagamento si effettua <strong>direttamente in auto</strong>, in contanti o con carta (POS a bordo): nessuna carta di credito richiesta al momento della prenotazione, nessun anticipo online. Prenota tramite il form del sito, WhatsApp o telefono e ricevi conferma in pochi minuti.</p>
  `,

  routesEyebrow: "Tratte più richieste",
  routesTitle: "Prezzi indicativi da Santa Teresa Gallura",
  from: "da",
  routes: [
    {
      icon: <Plane className="w-4 h-4" />,
      type: "Transfer aeroporto",
      title: "Aeroporto Olbia → Santa Teresa Gallura",
      desc: "Circa 65 km · 55-70 min. Attendiamo gratis fino a 60 min dopo l'atterraggio.",
      price: "€90",
    },
    {
      icon: <Ship className="w-4 h-4" />,
      type: "Transfer porto",
      title: "Santa Teresa → Porto Palau (traghetto La Maddalena)",
      desc: "Circa 25 km · 30 min. Perfetto per chi vuole visitare l'arcipelago.",
      price: "€45",
    },
    {
      icon: <Ship className="w-4 h-4" />,
      type: "Transfer Bonifacio",
      title: "Santa Teresa → Porto Bonifacio (Corsica)",
      desc: "Transfer al porto per il traghetto Moby / Blu Navy verso la Corsica.",
      price: "€20",
    },
    {
      icon: <Car className="w-4 h-4" />,
      type: "Tour privato",
      title: "Tour Capo Testa & Valle della Luna",
      desc: "2h · Panorami mozzafiato sulle Bocche di Bonifacio, spiaggia di Rena Bianca.",
      price: "€80",
    },
    {
      icon: <Car className="w-4 h-4" />,
      type: "Tour privato",
      title: "Santa Teresa → Costa Smeralda (Porto Cervo)",
      desc: "Giornata intera nelle spiagge iconiche della Gallura. Fermate a piacere.",
      price: "€180",
    },
    {
      icon: <Plane className="w-4 h-4" />,
      type: "Transfer aeroporto",
      title: "Santa Teresa → Aeroporto Alghero",
      desc: "Circa 140 km · 1h50. Ideale per voli internazionali low-cost.",
      price: "€170",
    },
  ],

  whyTitle: "Perché scegliere Gallura Drive per il tuo NCC",
  why: [
    {
      icon: <Clock className="w-5 h-5" />,
      title: "Puntualità garantita",
      desc: "Monitoriamo il tuo volo o traghetto in tempo reale. Se sei in ritardo, ti aspettiamo senza costi extra.",
    },
    {
      icon: <ShieldCheck className="w-5 h-5" />,
      title: "Tariffe trasparenti",
      desc: "Prezzo concordato prima della corsa. Nessun tassametro, nessun supplemento nascosto, IVA inclusa.",
    },
    {
      icon: <Star className="w-5 h-5" />,
      title: "Comfort superiore",
      desc: "Chrysler 300C Touring blu: interni in pelle, aria condizionata, Wi-Fi, acqua omaggio a bordo.",
    },
  ],

  faqTitle: "Domande frequenti sul servizio NCC a Santa Teresa Gallura",
  faq: [
    {
      q: "Quanto costa un NCC da Olbia a Santa Teresa Gallura?",
      a: "Il transfer NCC da Olbia Costa Smeralda Airport a Santa Teresa Gallura parte da 90€ per l'intera vettura (fino a 4 passeggeri, bagagli inclusi). Il prezzo è fisso e concordato prima della partenza. Il percorso di circa 65 km richiede 55-70 minuti a seconda del traffico. Prenota via sito, WhatsApp o telefono.",
    },
    {
      q: "Che differenza c'è tra taxi e NCC a Santa Teresa Gallura?",
      a: "Il taxi lavora su chiamata immediata con tassametro, mentre l'NCC (Noleggio Con Conducente) richiede prenotazione anticipata e ha tariffa concordata prima della corsa. L'NCC offre un veicolo dedicato più elegante, autista in giacca e cravatta, servizi premium come acqua omaggio e Wi-Fi, e la flessibilità di fermate su richiesta durante il tragitto.",
    },
    {
      q: "Come si prenota il servizio NCC?",
      a: "Puoi prenotare in tre modi: (1) tramite il form online sul sito galluradrive.it con conferma via email/WhatsApp entro poche ore, (2) su WhatsApp al +39 347 001 1246 per richieste last-minute, (3) telefonicamente allo stesso numero, disponibile 24/7. Consigliamo di prenotare con almeno 24 ore di anticipo, soprattutto in alta stagione (giugno-settembre).",
    },
    {
      q: "Il servizio è disponibile 24 ore su 24?",
      a: "Sì. Gallura Drive opera 24 ore su 24, 7 giorni su 7, tutto l'anno. Effettuiamo transfer notturni per voli con arrivo in tarda serata o partenze all'alba dall'aeroporto di Olbia, senza supplementi eccessivi (piccolo sovrapprezzo per corse tra le 23:00 e le 06:00).",
    },
    {
      q: "Come si paga? Serve la carta di credito online?",
      a: "No, nessuna carta è richiesta online. Il pagamento avviene direttamente in auto al momento del servizio, in contanti (EUR) oppure tramite POS a bordo (accettiamo carte di debito, credito, Bancomat, Visa, Mastercard). La prenotazione online non prevede alcun addebito anticipato.",
    },
    {
      q: "Coprite anche Bonifacio e la Corsica?",
      a: "Effettuiamo il transfer da Santa Teresa Gallura al porto di Santa Teresa (imbarco traghetto per Bonifacio) e viceversa. La traversata verso Bonifacio dura circa 50 minuti e va prenotata separatamente con Moby Lines o Blu Navy. Al ritorno possiamo attenderti al porto e riportarti al tuo hotel o all'aeroporto.",
    },
    {
      q: "L'auto ha spazio per bagagli e passeggeri?",
      a: "La Chrysler 300C Touring è una station wagon con bagagliaio da 630 litri (espandibile a 1.602 litri): comodamente 4 valigie grandi + bagagli a mano per 4 passeggeri. Per gruppi più numerosi o bagagli ingombranti (biciclette, tavole da surf) contattaci in fase di prenotazione per verificare le condizioni.",
    },
    {
      q: "Fate anche tour privati oltre ai semplici transfer?",
      a: "Assolutamente sì. Organizziamo tour giornalieri o di mezza giornata partendo da Santa Teresa Gallura: Capo Testa e Valle della Luna, spiagge di Rena Bianca e La Marmorata, arcipelago di La Maddalena (con imbarco a Palau), Costa Smeralda con Porto Cervo e Cala di Volpe. Fermate personalizzate su richiesta.",
    },
  ],

  finalTitle: "Pronto a prenotare il tuo NCC a Santa Teresa Gallura?",
  finalDesc:
    "Chiamaci, scrivici su WhatsApp o compila il form online: rispondiamo in pochi minuti, 24/7.",
};

/* ---------------- CONTENT — ENGLISH ---------------- */
const EN = {
  metaTitle:
    "Santa Teresa Gallura Taxi & Chauffeur | 24/7 Private Driver · Gallura Drive",
  metaDescription:
    "Private chauffeur (NCC) service in Santa Teresa Gallura with a blue Chrysler 300C. Olbia airport transfers, Bonifacio, Palau, Costa Smeralda. Book online, pay in the car.",
  eyebrow: "Private chauffeur · Santa Teresa Gallura",
  h1: "Santa Teresa Gallura Private Driver — Your NCC in North Sardinia",
  lead:
    "24/7 private chauffeur service (NCC) in Santa Teresa Gallura. Elegant transfers aboard our midnight-blue Chrysler 300C Touring: Olbia airport, Golfo Aranci port, Bonifacio ferry connection, private tours to Capo Testa, Rena Bianca and the whole Costa Smeralda.",
  zone: "North Gallura · Costa Smeralda · Strait of Bonifacio",
  f1: "Available 24/7",
  f2: "Licensed NCC driver",
  f3: "Chrysler 300C · up to 4 passengers",
  f4: "Verified reviews",
  ctaBook: "Book now",
  introTitle:
    "Private driver in Santa Teresa Gallura: American elegance, Sardinian hospitality",
  intro: `
    <p>Looking for a reliable <strong>private chauffeur in Santa Teresa Gallura</strong>? <strong>Gallura Drive</strong> is your trusted NCC (Noleggio Con Conducente) service in North Sardinia. We drive you aboard a <strong>midnight-blue Chrysler 300C Touring</strong>: an iconic American station wagon with leather interiors, automatic climate and a generous trunk — perfect for couples, families and small groups of up to 4 passengers.</p>

    <p>Unlike a traditional taxi, our NCC service guarantees <strong>a fixed price agreed before departure</strong>, advance booking, a dedicated vehicle and a suited driver who welcomes you with complimentary water and on-board Wi-Fi. No surprises, no waiting: the car arrives on time at the location you choose — hotel, airport, harbour or restaurant.</p>

    <p>We operate every day of the year, 24/7, from <strong>Santa Teresa Gallura</strong> with direct connections to <strong>Olbia Costa Smeralda Airport</strong>, <strong>Golfo Aranci port</strong>, <strong>Bonifacio</strong> (Corsica ferry), <strong>Palau</strong>, <strong>Cannigione</strong>, <strong>Baja Sardinia</strong>, <strong>Porto Cervo</strong> and the most beautiful beaches nearby: Rena Bianca, Capo Testa, La Marmorata, Cala Spinosa.</p>

    <p>Payment is made <strong>directly in the car</strong>, cash or card (POS on board): no credit card required at booking, no advance online payment. Book via the website form, WhatsApp or phone and receive confirmation within minutes.</p>
  `,

  routesEyebrow: "Most requested routes",
  routesTitle: "Indicative prices from Santa Teresa Gallura",
  from: "from",
  routes: [
    {
      icon: <Plane className="w-4 h-4" />,
      type: "Airport transfer",
      title: "Olbia Airport → Santa Teresa Gallura",
      desc: "About 65 km · 55-70 min. We wait for free up to 60 min after landing.",
      price: "€90",
    },
    {
      icon: <Ship className="w-4 h-4" />,
      type: "Port transfer",
      title: "Santa Teresa → Palau (La Maddalena ferry)",
      desc: "About 25 km · 30 min. Perfect for visiting the archipelago.",
      price: "€45",
    },
    {
      icon: <Ship className="w-4 h-4" />,
      type: "Bonifacio transfer",
      title: "Santa Teresa → Bonifacio Port (Corsica)",
      desc: "Transfer to the port for the Moby / Blu Navy ferry to Corsica.",
      price: "€20",
    },
    {
      icon: <Car className="w-4 h-4" />,
      type: "Private tour",
      title: "Capo Testa & Valley of the Moon tour",
      desc: "2h · Breathtaking views over the Strait of Bonifacio, Rena Bianca beach.",
      price: "€80",
    },
    {
      icon: <Car className="w-4 h-4" />,
      type: "Private tour",
      title: "Santa Teresa → Costa Smeralda (Porto Cervo)",
      desc: "Full day on the iconic Gallura beaches. Stops at your request.",
      price: "€180",
    },
    {
      icon: <Plane className="w-4 h-4" />,
      type: "Airport transfer",
      title: "Santa Teresa → Alghero Airport",
      desc: "About 140 km · 1h50. Ideal for international low-cost flights.",
      price: "€170",
    },
  ],

  whyTitle: "Why choose Gallura Drive for your NCC",
  why: [
    {
      icon: <Clock className="w-5 h-5" />,
      title: "Guaranteed punctuality",
      desc: "We monitor your flight or ferry in real time. If you're late, we wait at no extra cost.",
    },
    {
      icon: <ShieldCheck className="w-5 h-5" />,
      title: "Transparent rates",
      desc: "Price agreed before the ride. No taximeter, no hidden surcharge, VAT included.",
    },
    {
      icon: <Star className="w-5 h-5" />,
      title: "Superior comfort",
      desc: "Blue Chrysler 300C Touring: leather interiors, A/C, Wi-Fi, complimentary water on board.",
    },
  ],

  faqTitle: "Frequently asked questions — NCC in Santa Teresa Gallura",
  faq: [
    {
      q: "How much is a private transfer from Olbia to Santa Teresa Gallura?",
      a: "The NCC transfer from Olbia Costa Smeralda Airport to Santa Teresa Gallura starts from €90 for the whole vehicle (up to 4 passengers, luggage included). Price is fixed and agreed before departure. The ~65 km route takes 55-70 minutes depending on traffic. Book via website, WhatsApp or phone.",
    },
    {
      q: "What's the difference between a taxi and NCC in Santa Teresa Gallura?",
      a: "A taxi operates on-demand with a meter; NCC (chauffeur service) requires advance booking with a fixed price agreed before the ride. NCC offers a more elegant dedicated vehicle, a suited driver, premium services like complimentary water and Wi-Fi, plus flexibility for stops during the journey.",
    },
    {
      q: "How do I book the NCC service?",
      a: "Three ways: (1) online form on galluradrive.it with email/WhatsApp confirmation within hours, (2) WhatsApp at +39 347 001 1246 for last-minute requests, (3) phone at the same number, available 24/7. We recommend booking at least 24 hours in advance, especially in high season (June-September).",
    },
    {
      q: "Is the service available 24/7?",
      a: "Yes. Gallura Drive operates 24/7, all year round. We provide night transfers for late arrivals or dawn departures from Olbia Airport, with only a small surcharge between 11pm and 6am.",
    },
    {
      q: "How do I pay? Is a credit card required online?",
      a: "No, no card required online. Payment is made directly in the car at service time, in cash (EUR) or by POS on board (debit/credit cards, Visa, Mastercard accepted). Online booking involves no advance charge.",
    },
    {
      q: "Do you also cover Bonifacio and Corsica?",
      a: "We transfer you from Santa Teresa Gallura to the Santa Teresa port (Bonifacio ferry) and back. The crossing takes about 50 minutes and must be booked separately with Moby Lines or Blu Navy. On return we can pick you up at the port and drive you to your hotel or airport.",
    },
    {
      q: "Is there enough room for luggage and passengers?",
      a: "The Chrysler 300C Touring is a station wagon with a 630-litre trunk (up to 1,602 litres expanded): comfortably fits 4 large suitcases + carry-ons for 4 passengers. For bigger groups or oversized luggage (bikes, surfboards) please contact us at booking time.",
    },
    {
      q: "Do you also offer private tours, not just transfers?",
      a: "Absolutely. We organise half-day or full-day tours from Santa Teresa Gallura: Capo Testa and Valley of the Moon, Rena Bianca and La Marmorata beaches, La Maddalena archipelago (via Palau ferry), Costa Smeralda with Porto Cervo and Cala di Volpe. Custom stops on request.",
    },
  ],

  finalTitle: "Ready to book your NCC in Santa Teresa Gallura?",
  finalDesc:
    "Call us, message us on WhatsApp or fill in the online form: we reply within minutes, 24/7.",
};
